import React, { useCallback } from 'react';
import { ScrollView, TouchableOpacity, StyleSheet, Platform } from 'react-native';
import { Text } from '@/components/common/text';
import { useAppDispatch } from '@/store';
import { resetAuth } from '@/store/slices/authSlice';
import { SecureStorage } from '@/services/shared/storage/SecureStorage';
import { onColor } from '@/constants/shared/theme/onColor';
import { useThemedStyles } from '@/hooks/shared/useThemeColors';
import type { ThemeContextValue } from '@/hooks/shared/useThemeColors';

/**
 * Unified Auth — zero-context state (design §5.1, RES-89).
 *
 * Reached when an authenticated account enumerates no available contexts (e.g. a
 * terminated employee whose lease also ended). The backend normally 403s such a
 * login, so this is a rare, defensive fallback: show the message and let the
 * user sign out back to the login screen.
 */
export const NO_ACCESS_MESSAGE = 'No active access; contact your property manager.';

export const NoAccessScreen: React.FC = () => {
  const dispatch = useAppDispatch();
  const styles = useThemedStyles(makeStyles);

  const handleSignOut = useCallback(async () => {
    try {
      await SecureStorage.getInstance().clearTokens();
    } finally {
      dispatch(resetAuth());
    }
  }, [dispatch]);

  return (
    <ScrollView style={styles.scroll} contentContainerStyle={styles.container}>
      <Text style={styles.title}>No access</Text>
      <Text style={styles.message}>{NO_ACCESS_MESSAGE}</Text>
      <TouchableOpacity
        testID="no-access-sign-out"
        accessibilityRole="button"
        style={styles.button}
        onPress={handleSignOut}
        activeOpacity={0.8}
      >
        <Text style={styles.buttonText}>Sign out</Text>
      </TouchableOpacity>
    </ScrollView>
  );
};

const makeStyles = ({ colors, semantic }: ThemeContextValue) =>
  StyleSheet.create({
    scroll: {
      flex: 1,
      backgroundColor: colors.bg.base,
    },
    container: {
      flexGrow: 1,
      backgroundColor: colors.bg.base,
      alignItems: 'center',
      justifyContent: 'center',
      paddingHorizontal: 32,
    },
    title: {
      fontSize: 22,
      fontWeight: '700',
      color: colors.text.primary,
      marginBottom: 12,
      fontFamily: Platform.select({
        ios: 'Avenir-Heavy',
        android: 'sans-serif-black',
        default: 'System',
      }),
    },
    message: {
      fontSize: 15,
      color: colors.text.secondary,
      textAlign: 'center',
      lineHeight: 22,
      marginBottom: 32,
      fontFamily: Platform.select({
        ios: 'Avenir-Book',
        android: 'sans-serif',
        default: 'System',
      }),
    },
    button: {
      backgroundColor: semantic.interactive.primary,
      borderRadius: 8,
      paddingVertical: 14,
      paddingHorizontal: 32,
      alignItems: 'center',
      justifyContent: 'center',
    },
    buttonText: {
      color: onColor(semantic.interactive.primary),
      fontSize: 16,
      fontWeight: '600',
      fontFamily: Platform.select({
        ios: 'Avenir-Medium',
        android: 'sans-serif-medium',
        default: 'System',
      }),
    },
  });

export default NoAccessScreen;
